import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateRelocationMutation } from "@/app/Relocations/GraphQL/relocationMutations";
import {
  Currency,
  RelocationFieldsFragment,
  RelocationInclusionType,
  UpdateRelocationInput,
} from "@/gql/graphql";
import {
  RelocationInclusionForm,
  RelocationInclusionsList,
} from "@/app/Relocations/Forms/RelocationInclusionForm";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { relocationInclusionsValidation } from "@/app/Relocations/Validation/relocationValidation";
import { useTranslations } from "use-intl";
import { StarIcon } from "@heroicons/react/24/outline";

// The inclusion inputs read the currency off the form values to format amounts,
// so it rides along here even though only inclusions are submitted.
type InclusionsFormValues = UpdateRelocationInput & { currency: Currency };

export function RelocationInclusionsCard({
  relocation,
  excludedTypes = [],
}: {
  relocation: RelocationFieldsFragment;
  excludedTypes?: RelocationInclusionType[];
}) {
  const { mutateAsync } = useGqlMutation(updateRelocationMutation);
  const t = useTranslations("relocation.inclusions");

  return (
    <RecordScreenFormCard<InclusionsFormValues>
      containerClassName="rounded-lg border border-gray-200 bg-white p-6"
      title={
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-amber-100 p-2">
            <StarIcon className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <div>{t("cardTitle")}</div>
            <p className="text-sm font-normal text-gray-500">
              {t("cardSubtitle")}
            </p>
          </div>
        </div>
      }
      initialValues={{
        currency: relocation.currency,
        inclusions: {
          upsert: relocation.inclusions.map((inclusion) => ({
            id: inclusion.id,
            type: inclusion.type,
            description: inclusion.description || null,
            has_free_tank: inclusion.has_free_tank,
            value: inclusion.value,
          })),
          delete: [],
        },
      }}
      validationSchema={toFormikValidationSchema(
        relocationInclusionsValidation,
      )}
      onSubmit={async (values) => {
        await mutateAsync({
          id: relocation.id,
          input: {
            inclusions: values.inclusions,
          },
        });
      }}
      view={<RelocationInclusionsList relocation={relocation} />}
    >
      <RelocationInclusionForm excludedTypes={excludedTypes} />
    </RecordScreenFormCard>
  );
}
